import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { saveText } from '../../utils/download';
import * as S from '../specimens/styles';

/*
 * One run's sequence (migration 0085; GET/PUT /dna/runs/{id}/sequence): the
 * bases its result file holds, per read, for the primer it was sequenced
 * with. The server reads the run's processed (else raw) file -- FASTA,
 * FASTQ, GenBank, plain or an ABI chromatogram -- and a GET writes nothing;
 * whoever records DNA work may keep what was read, paste a sequence instead
 * (a PDF report has none to read), or forget it.
 *
 * Shown 60 bases a line in blocks of 10, numbered, left to right in either
 * language; with its length, GC% and, from a chromatogram or FASTQ, its
 * mean quality and the share at Q20 or better. Copy, FASTA download, the
 * reverse complement, and a BLAST search at NCBI with the read filled in.
 */
const COMPLEMENT = { A: 'T', T: 'A', U: 'A', G: 'C', C: 'G', R: 'Y', Y: 'R', K: 'M', M: 'K', S: 'S', W: 'W',
  B: 'V', V: 'B', D: 'H', H: 'D', N: 'N', '-': '-' };
export const reverseComplement = (seq) => [...String(seq || '')].reverse().map((b) => COMPLEMENT[b] || 'N').join('');
export const BLAST_MAX = 7000;

/** NCBI's nucleotide BLAST with the read filled in (opened by the person). */
export function blastUrl(seq) {
  const query = String(seq || '').slice(0, BLAST_MAX);
  return `https://blast.ncbi.nlm.nih.gov/Blast.cgi?PROGRAM=blastn&PAGE_TYPE=BlastSearch&LINK_LOC=blasthome&QUERY=${query}`;
}

/** The sequence as numbered lines of `width`, in blocks of 10. */
export function formatLines(seq, width = 60) {
  const lines = [];
  for (let i = 0; i < seq.length; i += width) {
    const chunk = seq.slice(i, i + width).match(/.{1,10}/g) || [];
    lines.push(`${String(i + 1).padStart(6, ' ')}  ${chunk.join(' ')}`);
  }
  return lines;
}

const fasta = (records, flip) => records.map((r, i) => {
  const seq = flip ? reverseComplement(r.sequence) : r.sequence;
  return `>${r.name || `read ${i + 1}`}${flip ? ' (reverse complement)' : ''}\n${seq.match(/.{1,70}/g)?.join('\n') || ''}\n`;
}).join('');

export default function SequenceViewer({ runId, runCode, canEdit = false, onChanged }) {
  const { t } = useTranslation('dna');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [flip, setFlip] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setError('');
    return api.get(`/dna/runs/${runId}/sequence`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.put(`/dna/runs/${runId}/sequence`, body);
      setData(r.data);
      setPasting(false);
      setPasted('');
      onChanged?.();
    } catch (e) {
      const code = e?.response?.data?.code;
      setError(code === 'sequence_unreadable' || code === 'no_sequence' ? t('genetic.unreadable') : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const records = data?.records || [];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fasta(records, flip));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError(t('genetic.copyFailed'));
    }
  };

  if (!data && !error) return <p style={S.hint}>{t('genetic.loading')}</p>;

  const primers = [data?.amplification_primers_name, data?.sequencing_primers_name].filter(Boolean);
  const fromFile = data && !data.stored && records.length > 0;
  return (
    <div style={box} data-testid={`sequence-${runId}`} role="region"
      aria-label={t('genetic.sequenceOf', { run: runCode || data?.run_code || '' })}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong>{t('genetic.sequence')}</strong>
        {data?.gene_name && <span style={S.hint}>{data.gene_name}</span>}
        {primers.length > 0 && (
          <span style={S.hint} data-testid="sequence-primers">{t('genetic.primers')}: <bdi dir="ltr">{primers.join(' / ')}</bdi></span>
        )}
        {data?.source && (
          <span style={S.hint} data-testid="sequence-source">
            {t(`genetic.source.${data.source}`)}{data.stored ? ` · ${t('genetic.kept')}` : ` · ${t('genetic.notKept')}`}
          </span>
        )}
      </div>

      {error && <p role="alert" style={S.error}>{error}</p>}
      {data?.error && records.length === 0 && (
        <p style={S.hint} data-testid="sequence-file-problem">{t(`genetic.fileProblem.${data.error.code}`,
          { defaultValue: data.error.message })}</p>
      )}
      {data && records.length === 0 && !data.error && (
        <p style={S.hint} data-testid="sequence-none">{t('genetic.none')}</p>
      )}
      {(data?.warnings || []).map((w) => <p key={w} style={S.hint}>{w}</p>)}

      {records.map((r, i) => {
        const seq = flip ? reverseComplement(r.sequence) : r.sequence;
        return (
          <div key={`${r.name}-${i}`} style={{ marginTop: 8 }} data-testid="sequence-record">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
              <bdi dir="ltr" style={{ ...S.mono, fontWeight: 600 }}>{r.name || t('genetic.read', { n: i + 1 })}</bdi>
              <span><bdi dir="ltr">{r.length} bp</bdi></span>
              {r.gc_percent != null && <span>GC <bdi dir="ltr">{r.gc_percent}%</bdi></span>}
              {r.mean_quality != null && (
                <span>{t('genetic.quality')} <bdi dir="ltr">Q{r.mean_quality}</bdi>
                  {r.q20_percent != null && <> · <bdi dir="ltr">{r.q20_percent}% ≥Q20</bdi></>}</span>
              )}
              <a href={blastUrl(seq)} target="_blank" rel="noreferrer" title={t('genetic.tip.blast')}
                data-testid="sequence-blast">{t('genetic.blast')}</a>
            </div>
            <pre dir="ltr" style={pre} data-testid="sequence-bases" tabIndex={0}>{formatLines(seq).join('\n')}</pre>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {records.length > 0 && (
          <>
            <button type="button" style={small} onClick={copy} title={t('genetic.tip.copy')}>
              {copied ? t('genetic.copied') : t('genetic.copy')}</button>
            <button type="button" style={small} title={t('genetic.tip.download')}
              onClick={() => saveText(fasta(records, flip), `${data?.run_code || runId}.fasta`, 'text/plain')}>
              {t('genetic.download')}</button>
            <button type="button" style={small} aria-pressed={flip} onClick={() => setFlip(!flip)}
              title={t('genetic.tip.reverse')} data-testid="sequence-reverse">{t('genetic.reverse')}</button>
          </>
        )}
        {canEdit && fromFile && (
          <button type="button" style={small} disabled={busy} data-testid="sequence-keep"
            onClick={() => save({ from_file: data.source === 'raw_file' ? 'raw' : 'processed' })}
            title={t('genetic.tip.keep')}>{t('genetic.keep')}</button>
        )}
        {canEdit && !pasting && (
          <button type="button" style={small} onClick={() => setPasting(true)} data-testid="sequence-paste-open"
            title={t('genetic.tip.paste')}>{t('genetic.paste')}</button>
        )}
        {canEdit && data?.stored && (
          <button type="button" style={small} disabled={busy} onClick={() => save({ sequence: null })}
            data-testid="sequence-forget" title={t('genetic.tip.forget')}>{t('genetic.forget')}</button>
        )}
      </div>

      {pasting && (
        <form style={{ display: 'grid', gap: 6, marginTop: 8 }}
          onSubmit={(e) => { e.preventDefault(); if (pasted.trim()) save({ sequence: pasted }); }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span>{t('genetic.pasteLabel')}</span>
            <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={5} dir="ltr"
              style={{ ...S.input, ...S.mono }} data-testid="sequence-paste" />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="submit" style={S.btn()} disabled={busy || !pasted.trim()} data-testid="sequence-paste-save">
              {t('genetic.pasteSave')}</button>
            <button type="button" style={S.ghostBtn} onClick={() => setPasting(false)}>{t('genetic.cancel')}</button>
          </div>
        </form>
      )}
    </div>
  );
}

const box = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, marginTop: 6,
  background: 'var(--c-bg-subtle)' };
const pre = { ...S.mono, fontSize: 12, lineHeight: 1.5, margin: '4px 0 0', padding: 8, overflowX: 'auto',
  background: 'var(--c-surface, transparent)', border: '1px solid var(--c-border)', borderRadius: 6,
  whiteSpace: 'pre', maxHeight: 320 };
const small = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
